import React from 'react';
import { useTranslation } from 'react-i18next';
import { NS } from '../../i18n';
import './DashboardSkeleton.css';

const Bone: React.FC<{ className: string }> = ({ className }) => (
  <span className={`dashboard-skeleton__bone ${className}`} />
);

/** One numbered section of the form: its badge, title, lead and what it asks for. */
const SectionSkeleton: React.FC<{ slot?: boolean }> = ({ slot = false }) => (
  <div className="dashboard-skeleton__section">
    <Bone className="dashboard-skeleton__badge" />
    <div className="dashboard-skeleton__section-body">
      <Bone className="dashboard-skeleton__line dashboard-skeleton__line--title" />
      <Bone className="dashboard-skeleton__line dashboard-skeleton__line--lead" />
      {slot ? (
        <span className="dashboard-skeleton__slot" />
      ) : (
        <Bone className="dashboard-skeleton__input" />
      )}
    </div>
  </div>
);

/**
 * The creation form (`dashboards/new`) while its chunk loads, in the frame
 * `DashboardCreatePage` draws: breadcrumb, then the card with the title, the
 * lead and the Create button, then the three numbered sections — the name, the
 * main table, and the dashed slot the connected tables wait in.
 *
 * Same paddings and breakpoints as the page (`DashboardCreatePage.css`,
 * `NumberedSection.css`), so the form lands where its skeleton was. No visible
 * text; its accessible name is translated (`loading.aria`).
 */
export const DashboardCreateSkeleton: React.FC = () => {
  const { t } = useTranslation(NS);

  return (
    <div
      className="dashboard-skeleton dashboard-skeleton--create"
      role="status"
      aria-busy="true"
      aria-label={t('loading.aria')}
      data-testid="dashboard-skeleton-create"
    >
      <div className="dashboard-skeleton__breadcrumb" aria-hidden="true">
        <Bone className="dashboard-skeleton__crumb" />
        <Bone className="dashboard-skeleton__crumb dashboard-skeleton__crumb--current" />
      </div>

      <div className="dashboard-skeleton__card" aria-hidden="true">
        <div className="dashboard-skeleton__card-head">
          <div className="dashboard-skeleton__heading">
            <Bone className="dashboard-skeleton__line dashboard-skeleton__line--h1" />
            <Bone className="dashboard-skeleton__line dashboard-skeleton__line--lead" />
          </div>
          <Bone className="dashboard-skeleton__cta" />
        </div>

        <div className="dashboard-skeleton__form">
          <SectionSkeleton />
          <SectionSkeleton />
          <SectionSkeleton slot />
        </div>
      </div>
    </div>
  );
};
